import React, { useEffect } from 'react';
import {
  Autocomplete,
  Box,
  InputAdornment,
  InputLabel,
  TextField,
  useTheme,
} from '@mui/material';
import { IconButton } from '@mui/material';
import { ComposableDeviceFormProps } from '../../../../components/ComposableDevice/ComposableDeviceForm/ComposableDeviceForm';
import { getPolyglot } from '../../../../i18n';
import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../../redux/commander/selectors';
import { getMachineTypeSelector } from '../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { useAppSelector } from '../../../../redux/store.model';
import { ErrorText } from '../../../../theme/components/Forms';
import { PlusIcon } from '../../../../theme/icons';
import RemoveIcon from '@mui/icons-material/Remove';
import _ from 'lodash';
import * as yup from 'yup';

export const ComposableDeviceFormFROven = (
  props: ComposableDeviceFormProps
) => {
  const { formik } = props;

  const polyglot = getPolyglot();
  const theme = useTheme();

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: formik.values.composableDevice?.machineTypeId,
    })
  );

  formik.values.composableDevice =
    formik.values.composableDevice || machineType?.labels;

  const commandersQuantity =
    Number(formik.values.composableDevice?.labels?.commandersQuantity) ||
    Number(machineType?.labels.commandersQuantity) ||
    0;

  const devices = useAppSelector((state) =>
    getAllAvailableDevicesToAddByGroupIdSelector(state, {
      groupId: formik.values.parentGroupID,
    })
  );
  const devicesValue =
    typeof formik.values?.devices === 'object' ? formik.values?.devices : [];

  const minValue = Number(machineType?.labels?.commandersMinQuantity) || 0;
  const maxValue = Number(machineType?.labels?.commandersMaxQuantity) || 0;
  const model =
    formik.values.composableDevice?.labels?.businessModel ||
    machineType?.labels?.businessModel ||
    '';
  const consumption =
    Number(formik.values.composableDevice?.labels?.consumption) ||
    Number(machineType?.labels?.consumption) ||
    0;

  const updateQuantity = async (newQuantity: number) => {
    formik.setFieldValue(
      'composableDevice.labels.commandersQuantity',
      newQuantity
    );
    await formik.validateField('devices');
  };

  useEffect(() => {
    const machineTypeKeys = Object.keys(machineType?.labels || {});
    const groupKeys = Object.keys(
      formik.values?.composableDevice?.labels || {}
    );
    if (!_.isEqual(machineTypeKeys, groupKeys)) {
      formik.setFieldValue('composableDevice.labels', machineType?.labels);
    }
  }, [machineType]);

  useEffect(() => {
    const consumptionPerDevice =
      formik.values?.devices?.reduce(
        (acc: Record<string, number>, device: string) => ({
          ...acc,
          [device]: consumption / commandersQuantity,
        }),
        {}
      ) || {};
    formik.setFieldValue(
      'composableDevice.labels.ST_COMPOSABLE_CONSUMPTION_DATA_KEY',
      JSON.stringify(consumptionPerDevice)
    );
  }, [commandersQuantity, consumption]);

  useEffect(() => {
    const devicesValidation = yup.object({
      devices: yup
        .array()
        .of(yup.string())
        .min(
          commandersQuantity,
          polyglot.t('fours_fringand.oven.devices_quantity_must_equal')
        )
        .required(polyglot.t('composable_device.devices_required'))
        .test(
          'devices-quantity',
          polyglot.t('fours_fringand.oven.devices_quantity_must_equal', {
            quantity: commandersQuantity,
          }),
          function (items) {
            const quantity =
              this.parent.composableDevice?.labels?.commandersQuantity || 0;

            if (!Array.isArray(items)) return false;
            return items.length === Number(quantity);
          }
        ),
    });

    props.setValidationSchema(props.validationSchema.concat(devicesValidation));
  }, []);

  return (
    <>
      <TextField
        name="name"
        label={polyglot.t('composable_device.name')}
        onChange={formik.handleChange}
        value={formik.values.name}
        required
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={
          formik.touched.name &&
          formik.errors.name && (
            <ErrorText>{polyglot.t(formik.errors.name)}</ErrorText>
          )
        }
      />
      <TextField
        name="composableDevice.labels.businessModel"
        label={polyglot.t('fours_fringand.oven.model')}
        onChange={formik.handleChange}
        value={model}
        required
      />
      <TextField
        name="composableDevice.labels.consumption"
        type="number"
        label={polyglot.t('fours_fringand.oven.consumption')}
        onChange={formik.handleChange}
        value={consumption}
        required
        helperText={polyglot.t('fours_fringand.oven.consumptionHelperText')}
        InputProps={{
          endAdornment: <InputAdornment position="end">Kw</InputAdornment>,
        }}
      />
      <Box>
        <InputLabel sx={{ fontSize: '1rem', mb: 1 }}>
          {polyglot.t('fours_fringand.oven.commanders_quantity')}
        </InputLabel>
        <IconButton
          disabled={commandersQuantity <= +minValue}
          onClick={() => {
            const newQuantity = Math.max(commandersQuantity - 1, +minValue);
            updateQuantity(newQuantity);
          }}
        >
          <Box
            sx={{
              borderRadius: '50%',
              backgroundColor:
                commandersQuantity <= +minValue
                  ? theme.palette.divider
                  : theme.palette.primary?.main,
              color:
                commandersQuantity <= +minValue
                  ? theme.palette.divider
                  : theme.palette.primary?.contrastText,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <RemoveIcon />
          </Box>
        </IconButton>
        <TextField
          name="composableDevice.labels.commandersQuantity"
          onChange={(event) => {
            const newQuantity = Number(event.target.value);
            if (newQuantity >= minValue && newQuantity <= maxValue)
              updateQuantity(newQuantity);
          }}
          type="number"
          value={commandersQuantity}
          required
          sx={{ input: { textAlign: 'center' } }}
          InputProps={{
            inputProps: { min: minValue, max: maxValue },
            style: { textAlign: 'center' },
          }}
        />
        <IconButton
          disabled={commandersQuantity >= +maxValue}
          onClick={() => {
            const newQuantity = Math.min(commandersQuantity + 1, +maxValue);
            updateQuantity(newQuantity);
          }}
          color="primary"
        >
          <Box
            sx={{
              borderRadius: '50%',
              backgroundColor:
                commandersQuantity >= +maxValue
                  ? theme.palette.divider
                  : theme.palette.primary?.main,
              color:
                commandersQuantity >= +maxValue
                  ? theme.palette.divider
                  : theme.palette.primary?.contrastText,
              width: 30,
              height: 30,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0 0.4rem 0',
              fontSize: '1rem',
            }}
          >
            <PlusIcon />
          </Box>
        </IconButton>
      </Box>
      {Array.from({ length: +commandersQuantity }).map((_, index) => (
        <Autocomplete
          key={index}
          id="devices-select-deck"
          options={devices}
          autoHighlight
          getOptionLabel={(option) => option.name || ''}
          value={
            devicesValue[index]
              ? devices.find((d) => d.uuid === devicesValue[index])
              : null
          }
          onChange={(_, value) => {
            const newDevices = formik.values.devices
              ? formik.values.devices
                  .slice(0, index)
                  .concat([value?.uuid || ''])
                  .concat(devicesValue.slice(index + 1))
                  .filter((device) => device !== '')
              : [];

            formik.setFieldValue('devices', newDevices);
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box key={key} component="li" {...optionProps}>
                {option.name || ''}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={polyglot.t('fours_fringand.oven.shelf', {
                number: index + 1,
              })}
            />
          )}
        />
      ))}
      {formik.errors.devices && <ErrorText>{formik.errors.devices}</ErrorText>}
    </>
  );
};
